// FieldVision - TrashModal.tsx
// Modal til visning og gendannelse af slettede kort
// Viser liste af slettede kort med Gendan-knap
// UPDATE: Alle kort (drone og satellit) kan nu gendannes
// NEW: Source indikator + permanent slet knap

import React, { useState, useEffect, useCallback } from 'react';

interface DeletedMap {
  Id: number;
  OrderId: number | null;
  Marknr: string;
  CVR: string;
  Type: string;
  MapFormat: string;
  Source: string;
  DeletedAt: string;
  CreatedAt: string;
  CaptureDate: string;
}

interface RestoreResponse {
  success: boolean;
  message: string;
  map: {
    Id: number;
    Marknr: string;
    CVR: string;
  };
}

interface TrashModalProps {
  onClose: () => void;
  onMapRestored?: () => void;
}

// Helper: Check om kort er satellit-baseret
const isSatelliteSource = (source: string): boolean => {
  if (!source) return false;
  const s = source.toLowerCase();
  return s.includes('satellite') || s.includes('sentinel') || s === 'ndre';
};

const TrashModal: React.FC<TrashModalProps> = ({ onClose, onMapRestored }) => {
  const [maps, setMaps] = useState<DeletedMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [restoreMessage, setRestoreMessage] = useState<string | null>(null);

  // Hent slettede kort
  const loadDeletedMaps = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/zonemaps/deleted`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke hente slettede kort');
      }

      const data: DeletedMap[] = await response.json();
      setMaps(data);
    } catch (err) {
      console.error('Error loading deleted maps:', err);
      setError('Der opstod en fejl ved hentning af slettede kort');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDeletedMaps();
  }, [loadDeletedMaps]);

  // Gendan kort
  const handleRestore = async (mapId: number) => {
    setRestoringId(mapId);
    setRestoreMessage(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/zonemaps/${mapId}/restore`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke gendanne kort');
      }

      const result: RestoreResponse = await response.json();

      // Fjern kortet fra listen
      setMaps(prevMaps => prevMaps.filter(m => m.Id !== mapId));

      // Vis besked
      setRestoreMessage(`Kort for mark ${result.map.Marknr} er gendannet`);

      // Fjern besked efter 3 sekunder
      setTimeout(() => {
        setRestoreMessage(null);
      }, 3000);

      // Informer parent
      if (onMapRestored) {
        onMapRestored();
      }
    } catch (err) {
      console.error('Error restoring map:', err);
      setError('Der opstod en fejl ved gendannelse af kortet');
    } finally {
      setRestoringId(null);
    }
  };

  // Permanent slet kort
  const handlePermanentDelete = async (mapId: number) => {
    if (!window.confirm('Er du sikker? Kortet slettes permanent og kan IKKE gendannes.')) {
      return;
    }

    setDeletingId(mapId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:4002'}/api/zonemaps/${mapId}/permanent`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        setMaps(prev => prev.filter(m => m.Id !== mapId));
      } else {
        const data = await response.json();
        alert(data.error || 'Kunne ikke slette kortet');
      }
    } catch (err) {
      console.error('Error permanently deleting map:', err);
      alert('Kunne ikke slette kortet');
    } finally {
      setDeletingId(null);
    }
  };

  // Formater dato
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('da-DK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Formater kort-type
  const getTypeLabel = (type: string): string => {
    switch (type?.toLowerCase()) {
      case 'ndvi':
        return 'NDVI';
      case 'ndre':
        return 'NDRE';
      case 'weed':
        return 'Ukrudt';
      case 'regrowth':
        return 'Genvaekst';
      default:
        return type || 'Kort';
    }
  };

  // Beregn tid siden sletning
  const getTimeSinceDelete = (dateStr: string): string => {
    if (!dateStr) return '';

    const now = new Date();
    const deleted = new Date(dateStr);
    const diffMs = now.getTime() - deleted.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return diffDays === 1 ? '1 dag siden' : `${diffDays} dage siden`;
    } else if (diffHours > 0) {
      return diffHours === 1 ? '1 time siden' : `${diffHours} timer siden`;
    } else if (diffMinutes > 0) {
      return diffMinutes === 1 ? '1 minut siden' : `${diffMinutes} minutter siden`;
    }
    return 'Lige nu';
  };

  return (
    <div className="trash-modal-overlay" onClick={onClose}>
      <div className="trash-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="trash-modal-header">
          <div className="trash-modal-title">
            <svg xmlns="http://www.w3.org/2000/svg" height="28px" viewBox="0 -960 960 960" width="28px" fill="#333333" style={{verticalAlign: "middle"}}><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
            <h2>Papirkurv</h2>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Luk">
            &times;
          </button>
        </div>

        {/* Besked */}
        {restoreMessage && (
          <div className="restore-message">
            <span className="restore-icon">&#10003;</span>
            {restoreMessage}
          </div>
        )}

        {/* Indhold */}
        <div className="trash-modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Henter slettede kort...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadDeletedMaps} className="retry-btn">
                Proev igen
              </button>
            </div>
          ) : maps.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">&#128464;</div>
              <h3>Papirkurven er tom</h3>
              <p>Slettede kort vises her og kan gendannes.</p>
            </div>
          ) : (
            <div className="deleted-maps-list">
              <p className="list-info">
                {maps.length} {maps.length === 1 ? 'kort' : 'kort'} i papirkurven
              </p>
              {maps.map(map => (
                <div key={map.Id} className="deleted-map-item">
                  <div className="map-info">
                    <div className="map-title">
                      <span className={`source-badge ${isSatelliteSource(map.Source) ? 'satellite' : 'drone'}`}>
                        {isSatelliteSource(map.Source) ? (
                          <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor"><path d="M560-32v-80q117 0 198.5-81.5T840-392h80q0 75-28.5 140.5t-77 114q-48.5 48.5-114 77T560-32Zm0-160v-80q50 0 85-35t35-85h80q0 83-58.5 141.5T560-192ZM222-57q-15 0-30-6t-27-17L23-222q-11-12-17-27t-6-30q0-16 6-30.5T23-335l127-127q23-23 57-23.5t57 22.5l50 50 28-28-50-50q-23-23-23-56t23-56l57-57q23-23 56.5-23t56.5 23l50 50 28-28-50-50q-23-23-23-56.5t23-56.5l127-127q12-12 27-18t30-6q15 0 29.5 6t26.5 18l142 142q12 11 17.5 25.5T895-730q0 15-5.5 30T872-673L745-546q-23 23-56.5 23T632-546l-50-50-28 28 50 50q23 23 22.5 56.5T603-405l-56 56q-23 23-56.5 23T434-349l-50-50-28 28 50 50q23 23 22.5 57T405-207L278-80q-11 11-25.5 17T222-57Zm0-79 42-42-142-142-42 42 142 142Zm85-85 42-42-142-142-42 42 142 142Zm184-184 56-56-142-142-56 56 142 142Zm198-198 42-42-142-142-42 42 142 142Zm85-85 42-42-142-142-42 42 142 142ZM448-504Z"/></svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor"><path d="M160-160q0-75 41-134.5T307-381l-10-99H160v-240H40v-80h320v80H240v160h48l-8-80h400l-8 80h48v-160H600v-80h321v80H801v240H664l-10 99q65 27 105.5 86.5T800-160h-80q0-66-47-113t-113-47H400q-66 0-113 47t-47 113h-80Zm225-240h189l17-160H368l17 160Zm0 0h189-189Z"/></svg>
                        )}
                      </span>
                      <span className="map-type">{getTypeLabel(map.Type)}</span>
                      <span className="map-marknr">Mark {map.Marknr}</span>
                    </div>
                    <div className="map-meta">
                      <span className="capture-date">
                        Oprettet: {formatDate(map.CaptureDate || map.CreatedAt)}
                      </span>
                      <span className="deleted-date">
                        Slettet: {getTimeSinceDelete(map.DeletedAt)}
                      </span>
                    </div>
                  </div>
                  <div className="map-actions">
                    <button
                      className="restore-btn"
                      onClick={() => handleRestore(map.Id)}
                      disabled={restoringId === map.Id || deletingId === map.Id}
                    >
                      {restoringId === map.Id ? (
                        <>
                          <span className="btn-spinner"></span>
                          Gendanner...
                        </>
                      ) : (
                        'Gendan'
                      )}
                    </button>
                    <button
                      className="delete-permanent-btn"
                      onClick={() => handlePermanentDelete(map.Id)}
                      disabled={restoringId === map.Id || deletingId === map.Id}
                      title="Slet permanent"
                    >
                      {deletingId === map.Id ? '...' : <svg xmlns="http://www.w3.org/2000/svg" height="18px" viewBox="0 -960 960 960" width="18px" fill="currentColor"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="trash-modal-footer">
          <p className="footer-note">
            Alle kort kan gendannes fra papirkurven.
          </p>
        </div>
      </div>

      <style>{`
        .trash-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .trash-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 520px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }

        .trash-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
        }

        .trash-modal-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .trash-icon-large {
          font-size: 28px;
        }

        .trash-modal-title h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #111827;
        }

        .close-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: transparent;
          border: none;
          font-size: 28px;
          color: #6b7280;
          cursor: pointer;
          border-radius: 50%;
          transition: all 0.15s;
        }

        .close-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .restore-message {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 16px 24px 0;
          padding: 12px 16px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          color: #16a34a;
          font-size: 14px;
          font-weight: 500;
          animation: slideIn 0.2s ease;
        }

        .restore-icon {
          font-size: 18px;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .trash-modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
        }

        .loading-state,
        .error-state,
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: #6b7280;
        }

        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e5e7eb;
          border-top-color: #16a34a;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .retry-btn {
          margin-top: 12px;
          padding: 10px 20px;
          background: #3c6c34;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
        }

        .retry-btn:hover {
          background: #2A4C24;
        }

        .empty-icon {
          font-size: 56px;
          margin-bottom: 16px;
          opacity: 0.6;
        }

        .empty-state h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: #374151;
        }

        .empty-state p {
          margin: 0;
          font-size: 14px;
          max-width: 280px;
        }

        .list-info {
          margin: 0 0 16px 0;
          font-size: 14px;
          color: #6b7280;
        }

        .deleted-maps-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .deleted-map-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          transition: border-color 0.15s;
        }

        .deleted-map-item:hover {
          border-color: #d1d5db;
        }

        .map-info {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .map-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .source-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          font-size: 14px;
        }

        .source-badge.satellite {
          background: #fef3c7;
        }

        .source-badge.drone {
          background: #dbeafe;
        }

        .map-type {
          font-weight: 600;
          font-size: 15px;
          color: #111827;
        }

        .map-marknr {
          padding: 2px 8px;
          background: #e5e7eb;
          color: #374151;
          font-size: 12px;
          font-weight: 500;
          border-radius: 4px;
        }

        .map-meta {
          display: flex;
          flex-direction: column;
          gap: 2px;
          font-size: 12px;
          color: #6b7280;
        }

        .deleted-date {
          color: #dc2626;
        }

        .map-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .restore-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: #3c6c34;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          color: white;
          cursor: pointer;
          transition: background 0.15s;
          white-space: nowrap;
        }

        .restore-btn:hover:not(:disabled) {
          background: #2A4C24;
        }

        .restore-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .delete-permanent-btn {
          background: none;
          border: 1px solid #ef4444;
          color: #ef4444;
          padding: 6px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .delete-permanent-btn:hover:not(:disabled) {
          background: #fef2f2;
        }

        .delete-permanent-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        .trash-modal-footer {
          padding: 16px 24px;
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
        }

        .footer-note {
          margin: 0;
          font-size: 12px;
          color: #6b7280;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default TrashModal;
